import type { ImageSlotProfile } from '../features/media/image-slots.js';
import type { NormalizedCrop } from '../features/media/image-crop.js';
import type { UploadFile } from './media-upload.js';

type DecodedImage = {
  width: number;
  height: number;
  source: CanvasImageSource;
  close(): void;
};

type ImageVariantDependencies = {
  decode(file: Blob): Promise<DecodedImage>;
  encode(
    source: CanvasImageSource,
    crop: SourceCrop,
    width: number,
    height: number,
    quality: number,
  ): Promise<Blob>;
  checksum(file: Blob): Promise<string>;
};

type SourceCrop = { x: number; y: number; width: number; height: number };

export type PreparedResponsiveVariant = {
  file: UploadFile;
  checksum: string;
  width: number;
  height: number;
};

export async function uploadResponsiveImage<Asset extends { id: string }>(input: {
  file: UploadFile;
  alt: string;
  profile: ImageSlotProfile;
  crop?: NormalizedCrop;
  prepare?: typeof prepareResponsiveImage;
  uploadVariant(variant: PreparedResponsiveVariant, alt: string): Promise<Asset>;
  register(canonicalAssetId: string, variants: { assetId: string; width: number }[]): Promise<void>;
  onProgress?(completed: number, total: number): void;
}) {
  const variants = await (input.prepare ?? prepareResponsiveImage)(input.file, input.profile, {
    crop: input.crop,
  });
  const uploaded: { asset: Asset; width: number }[] = [];
  for (const variant of variants) {
    uploaded.push({ asset: await input.uploadVariant(variant, input.alt), width: variant.width });
    input.onProgress?.(uploaded.length, variants.length);
  }
  const canonical = uploaded.at(-1);
  if (!canonical) throw new Error('Responsive image preparation produced no variants');
  await input.register(
    canonical.asset.id,
    uploaded.map(({ asset, width }) => ({ assetId: asset.id, width })),
  );
  return canonical.asset;
}

const WEBP_QUALITY = 0.82;
const LOCAL_SOURCE_MAX_BYTES = 64 * 1024 * 1024;

export async function prepareResponsiveImage(
  file: UploadFile,
  profile: ImageSlotProfile,
  options: {
    crop?: NormalizedCrop;
    dependencies?: ImageVariantDependencies;
  } = {},
): Promise<PreparedResponsiveVariant[]> {
  if (file.type !== 'image/jpeg' && file.type !== 'image/webp') {
    throw new Error('Choose a JPEG or WebP photograph');
  }
  if (file.size <= 0 || file.size > LOCAL_SOURCE_MAX_BYTES) {
    throw new Error('The source photograph must be no larger than 64 MB');
  }
  const dependencies = options.dependencies ?? browserImageDependencies;
  const decoded = await dependencies.decode(file);
  try {
    const requiredWidth = profile.widths[1];
    const crop = sourceCrop(options.crop, decoded.width, decoded.height);
    const cropRatio = crop.width / crop.height;
    const requiredHeight = Math.max(1, Math.round(requiredWidth / cropRatio));
    if (options.crop && (crop.width < requiredWidth || crop.height < requiredHeight)) {
      throw new Error(
        `The selected crop must contain at least ${requiredWidth} px wide for this placement`,
      );
    }
    if (!options.crop && decoded.width < requiredWidth) {
      throw new Error(`This image must be at least ${requiredWidth} px wide for this placement`);
    }
    const widths = [...profile.widths];
    const baseName = safeBasename(file.name);
    const variants: PreparedResponsiveVariant[] = [];
    for (const width of widths) {
      const height = Math.max(1, Math.round(width / cropRatio));
      const blob = await dependencies.encode(decoded.source, crop, width, height, WEBP_QUALITY);
      const output = Object.assign(blob, { name: `${baseName}-${width}w.webp` }) as UploadFile;
      variants.push({
        file: output,
        checksum: await dependencies.checksum(output),
        width,
        height,
      });
    }
    return variants;
  } finally {
    decoded.close();
  }
}

const browserImageDependencies: ImageVariantDependencies = {
  async decode(file) {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      close: () => bitmap.close(),
    };
  },
  encode(source, crop, width, height, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return Promise.reject(new Error('Image preparation is unavailable'));
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('WebP preparation failed'))),
        'image/webp',
        quality,
      );
    });
  },
  async checksum(file) {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  },
};

function sourceCrop(crop: NormalizedCrop | undefined, width: number, height: number): SourceCrop {
  if (!crop) return { x: 0, y: 0, width, height };
  const values = [crop.x, crop.y, crop.width, crop.height];
  if (
    values.some((value) => !Number.isFinite(value)) ||
    crop.x < 0 ||
    crop.y < 0 ||
    crop.width <= 0 ||
    crop.height <= 0 ||
    crop.x + crop.width > 1.000_001 ||
    crop.y + crop.height > 1.000_001
  ) {
    throw new Error('The selected crop is invalid');
  }
  return {
    x: Math.round(crop.x * width),
    y: Math.round(crop.y * height),
    width: Math.round(crop.width * width),
    height: Math.round(crop.height * height),
  };
}

function safeBasename(filename: string) {
  const withoutExtension = filename.replace(/\.[^.]+$/, '');
  const normalized = withoutExtension
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'photograph';
}
